import {
  normalizeLocaleLanguage,
  type SupportedLocaleLanguage,
} from "@/constants/localization";
import { SupportedLanguages } from "@/enums/internationalization";
import { buildPreferenceCookieWrite } from "@/utils/common/preferenceCookie";

/**
 * UI SSR language preference cookie. Host-only (no Domain) — not sent to
 * Galactus (`NEXT_PUBLIC_GAIL_API` / api.meetgail.com).
 */
export const LANGUAGE_COOKIE_NAME = "gail_lang";

/**
 * Normalize a raw cookie value to a supported i18n language code, or `null`
 * when missing / blank.
 */
export const parseLanguageCookieValue = (
  value: string | null | undefined
): SupportedLocaleLanguage | null => {
  if (value == null || value.trim() === "") {
    return null;
  }
  return normalizeLocaleLanguage(value);
};

/**
 * Read `gail_lang` from a raw Cookie header string (server-side).
 */
export const readLanguageFromCookieHeader = (
  cookieHeader: string | undefined
): SupportedLocaleLanguage | null => {
  if (!cookieHeader) return null;
  const match = cookieHeader
    .split(";")
    .map((part) => part.trim())
    .find((part) => part.startsWith(`${LANGUAGE_COOKIE_NAME}=`));
  if (!match) return null;
  const raw = decodeURIComponent(match.slice(LANGUAGE_COOKIE_NAME.length + 1));
  return parseLanguageCookieValue(raw);
};

/**
 * Read `gail_lang` from `document.cookie` (client-side). Returns `null` during
 * SSR when `document` is unavailable.
 */
export const readLanguageFromDocumentCookie = (): SupportedLocaleLanguage | null => {
  if (globalThis.document === undefined) return null;
  return readLanguageFromCookieHeader(globalThis.document.cookie);
};

const readLanguageFromLegacyLocalStorage = (): SupportedLocaleLanguage | null => {
  if (globalThis.window === undefined) return null;
  try {
    const cached = globalThis.localStorage.getItem("userProfile");
    if (!cached) return null;
    const parsed = JSON.parse(cached) as { language?: string };
    if (typeof parsed?.language !== "string") return null;
    return normalizeLocaleLanguage(parsed.language);
  } catch {
    return null;
  }
};

/**
 * Persist the user's language so the next SSR request and the client's module
 * init agree on the initial `lng`.
 */
export const writeLanguageCookie = (language: string): void => {
  if (globalThis.document === undefined) return;
  const normalized = normalizeLocaleLanguage(language);
  globalThis.document.cookie = buildPreferenceCookieWrite(
    LANGUAGE_COOKIE_NAME,
    normalized
  );
};

/**
 * Initial i18n language for module init: cookie first, then one-time migrate
 * from legacy `userProfile` localStorage, else English.
 */
export const getInitialI18nLanguage = (): SupportedLocaleLanguage => {
  const fromCookie = readLanguageFromDocumentCookie();
  if (fromCookie) return fromCookie;

  const fromLegacy = readLanguageFromLegacyLocalStorage();
  if (fromLegacy) {
    writeLanguageCookie(fromLegacy);
    return fromLegacy;
  }

  return SupportedLanguages.English;
};
